import type { ReactNode } from 'react';

import { ReplyDraftHeader, useHeaderOverlayHeight } from './ReplyDraftHeader';
import { ReplyDraftWizard } from './ReplyDraftWizard';
import { useOfferedReplyDraft } from '../../hooks/useOfferedReplyDraft';
import type { ComposerAccess } from '../../model/composer';
import { REPLY_DRAFT_COPY } from '../copy';

import { Button, ButtonText } from '@/components/ui/button';
import { Center } from '@/components/ui/center';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

type OfferedReplyDraftProps = {
  conversationId: string;
  composer: ComposerAccess;
  onClose: () => void;
  /** Space under the draft, such as the phone's keyboard and home indicator spacer. */
  bottomSpace?: ReactNode;
};

/**
 * What `.draft` opens. The shortcut is always offered to an expert, so the
 * draft may not be known yet: this shows the surface at once with a spinner,
 * then the wizard when the draft is found. When the list loads without one,
 * it says so rather than leaving the expert with a spinner that never ends.
 */
export function OfferedReplyDraft({
  conversationId,
  composer,
  onClose,
  bottomSpace,
}: OfferedReplyDraftProps) {
  const { replyDraftId, isLoading, isError, refetch } = useOfferedReplyDraft(conversationId);

  const headerOverlayHeight = useHeaderOverlayHeight();

  if (replyDraftId) {
    return (
      <ReplyDraftWizard
        replyDraftId={replyDraftId}
        conversationId={conversationId}
        composer={composer}
        onClose={onClose}
        bottomSpace={bottomSpace}
      />
    );
  }

  let body;

  if (isLoading) {
    body = (
      <Center accessibilityLiveRegion="polite">
        <Spinner size="large" accessibilityLabel={REPLY_DRAFT_COPY.loading} />
      </Center>
    );
  } else {
    body = (
      <VStack className="items-center gap-3 px-6" accessibilityLiveRegion="polite">
        <Text className="text-center text-typography-900">
          {isError ? REPLY_DRAFT_COPY.loadFailed : REPLY_DRAFT_COPY.noDraft}
        </Text>
        <Button
          variant="outline"
          action="secondary"
          className="min-h-12"
          onPress={isError ? refetch : onClose}>
          <ButtonText>{isError ? REPLY_DRAFT_COPY.retry : REPLY_DRAFT_COPY.close}</ButtonText>
        </Button>
      </VStack>
    );
  }

  return (
    <>
      <VStack className="h-full w-full flex-1" testID="reply-draft">
        <ReplyDraftHeader title={REPLY_DRAFT_COPY.title} onClose={onClose} />

        <Center className="flex-1" style={{ paddingTop: headerOverlayHeight }}>
          {body}
        </Center>
      </VStack>

      {bottomSpace}
    </>
  );
}
